// Approved copy for the three landing-page consolidation tables (AB#17146).
// Table lines and calc guides are verbatim from the ticket (source: Compliance
// Center — Table-Level Descriptions & Calc Guides §2.2); do not edit casually.

export const TAB_NAMES = {
    inspection: 'Assets Due for Inspection',
    underRepair: 'Under Repair Systems',
    chronic: 'Chronically Leaking Systems',
};

export const TABLE_LINES = {
    inspection:
        'Every inspection coming due or overdue, across all five regulations that set an inspection ' +
        'schedule. The Regulation column says which rule each row is owed under; an asset in scope of ' +
        'more than one rule appears once per rule, because those are different obligations rather than duplicates.',
    underRepair:
        'Assets with an unresolved leak repair. This list is informational and says nothing about ' +
        'whether anything is overdue. It shows every enabled, active asset that falls under at least ' +
        'one regulation and whose Operational Status is Under Repair.',
    chronic:
        'Every chronically leaking system across both federal rules. The Regulation column says whether ' +
        'a row is EPA Section 608 or the EPA AIM Act; an asset in scope of both appears under each, ' +
        'because the two obligations are reported separately.',
};

export const CALC_GUIDES = {
    inspection: [
        {
            lead: 'How we decide:',
            text: 'the same test each regulation’s own tile uses — days since the most recent leak ' +
                'inspection, measured against the cadence for that asset’s charge band under that rule.',
        },
        {
            lead: 'Why rows differ:',
            text: 'cadences and trigger bases are not the same across rules, which is why Required Cadence ' +
                'and Trigger Basis are shown by default: they are what explains why a federal row exists only ' +
                'after an exceedance while a state row is routine.',
        },
        // AB#17309 AC 6: retired-equipment clause, verbatim.
        {
            lead: 'Retired equipment:',
            text: 'retired or removed equipment is not listed. Nothing is due on equipment that’s gone.',
        },
        {
            lead: 'What to do:',
            text: 'work the overdue rows first, then open the regulation itself for the full obligation picture.',
        },
    ],
    underRepair: [
        {
            lead: 'How we decide:',
            text: 'the asset carries at least one regulation applicability tag, and its Operational Status is ' +
                'Under Repair — that is, a leak repair is open and not yet resolved on the asset. There is ' +
                'no leak-rate trigger, no repair clock and no status band on this table.',
        },
        {
            lead: 'Why some rows have no deadline anywhere:',
            text: 'because membership keys off applicability alone, assets under New Jersey and South Coast ' +
                'AQMD appear here too, and neither rule sets a repair deadline.',
        },
        {
            lead: 'What to do:',
            text: 'for the repair deadline itself, open the regulation and use its Leak Repair Deadline ' +
                'Exceeded tile; this tab tells you which regulated assets still have a leak repair open.',
        },
    ],
    chronic: [
        {
            lead: 'How we decide:',
            text: 'the same test each regulation’s own tile uses — refrigerant added in the calendar ' +
                'year against 125% of full charge, with the on-pace band starting at day 90.',
        },
        {
            lead: 'Why an asset can appear twice under one rule:',
            text: 'Calendar Year separates the year in progress from the closed year that is still reportable ' +
                'until March 1.',
        },
        // AB#17309 AC 6: retired-systems clause, verbatim.
        {
            lead: 'Retired systems:',
            text: 'retired or removed systems stay listed until June 30 of the following year, so the ' +
                'closed-year report can be completed.',
        },
        {
            lead: 'What to do:',
            text: 'rows at or over 125% are reportable; on-pace rows are the ones you can still change.',
        },
    ],
};

// Empty / filtered states (AC 9, Table and Field Spec §10). State A carries no count
// and makes no compliance claim; state C always names the count being hidden, with
// the wording keyed to which control is responsible.
export const getEmptyStateCopy = (tabName) => ({ filtersCount, searchTerm, totalCount }) => {
    const hasSearch = Boolean(searchTerm && searchTerm.length);
    const hasFilters = filtersCount > 0;
    const count = totalCount ?? 0;

    if (!hasSearch && !hasFilters) {
        return {
            noResultText: `No ${tabName}`,
            noResultText2: 'There are currently no assets that meet the criteria for this table.',
        };
    }
    if (hasSearch && hasFilters) {
        return {
            noResultText: 'No assets match your search and filters',
            noResultText2: `No results for “${searchTerm}” with ${filtersCount} filters applied. Clear them to see all ${count} ${tabName}.`,
        };
    }
    if (hasSearch) {
        return {
            noResultText: 'No assets match your search',
            noResultText2: `No results for “${searchTerm}”. Clear the search to see all ${count} ${tabName}.`,
        };
    }
    return {
        noResultText: 'No assets match your filters',
        noResultText2: `${filtersCount} filters are applied. Clear them to see all ${count} ${tabName}.`,
    };
};
